import React, { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Box, Typography, Toolbar, useMediaQuery, Button, Grid, Paper, Container, Chip, Card, CardContent } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { motion } from "framer-motion";
import {
  Flag,
  Collections,
  RateReview,
  CalendarMonth,
  SmartToy,
  Group,
  Extension,
  BarChart,
  Link,
  Contacts,
  SyncAlt,
  Palette,
  AutoAwesome,
  Brush
} from "@mui/icons-material";
import HomeHeader from "../../../Components/HomeHeader";
import HomeFooter from "../../../Components/HomeFooter";
import routes from "../../../Config/routes";
import createImage from "../../../Components/globalAssets/lightning.gif";
import heroImage from "../../../Components/globalAssets/create_hero.png";
const fadeIn = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };
const slideIn = { hidden: { opacity: 0, x: -50 }, visible: { opacity: 1, x: 0 } };
const scaleIn = { hidden: { opacity: 0, scale: 0.8 }, visible: { opacity: 1, scale: 1 } };

const stats = [
  { value: "1K+", label: "Campaigns Launched", icon: <Flag /> },
  { value: "1500K+", label: "Creators Onboarded", icon: <Brush /> },
  { value: "98%", label: "Collaboration Success", icon: <AutoAwesome /> },
  { value: "48h", label: "Avg Response Time", icon: <Palette /> }
];

const modules = [
  {
    icon: <Flag fontSize="large" />,
    title: "Creative Briefs & Templates",
    description: "Dynamic brief builder with pre-configured social, video, and email templates; one-click campaign launch.",
    color: "#FF6B6B",
    category: "Planning"
  },
  {
    icon: <Collections fontSize="large" />,
    title: "Asset Library & Versioning",
    description: "Centralized media repo with tags, side-by-side drafts, timestamped comments, and locked archives.",
    color: "#4ECDC4",
    category: "Organization"
  },
  {
    icon: <RateReview fontSize="large" />,
    title: "Review & Approval Workflows",
    description: "Multi-stage sign-off gates and in-app annotations for stakeholders to comment, draw, and approve.",
    color: "#45B7D1",
    category: "Workflow"
  },
  {
    icon: <CalendarMonth fontSize="large" />,
    title: "Content Calendar & Publishing",
    description: "Drag-drop scheduler plus cross-platform auto-publish to Instagram, TikTok, LinkedIn, and more.",
    color: "#96CEB4",
    category: "Scheduling"
  },
  {
    icon: <SmartToy fontSize="large" />,
    title: "AI-Assisted Creation",
    description: "Smart copy & caption generator and one-click reflow for 1:1, 9:16, 16:9, or custom aspect ratios.",
    color: "#FFEAA7",
    category: "AI Tools"
  },
  {
    icon: <Group fontSize="large" />,
    title: "Team Collaboration & Roles",
    description: "Task/comment threads, real-time status, and granular permissions for drafting, reviewing, or viewing.",
    color: "#DDA0DD",
    category: "Team"
  },
  {
    icon: <Extension fontSize="large" />,
    title: "Integrations & Extensions",
    description: "Sync with Google Drive, Figma, Adobe CC; push notifications to Slack, Teams, or in-app alerts.",
    color: "#98D8C8",
    category: "Connect"
  },
  {
    icon: <BarChart fontSize="large" />,
    title: "Reporting & Insights",
    description: "Engagement dashboards, ROI tracking, and content-to-budget tagging for direct revenue impact.",
    color: "#F7DC6F",
    category: "Analytics"
  },
  {
    icon: <Link fontSize="large" />,
    title: "Link-in-Bio Tools",
    description: "Build branded microsites, A/B test headlines/buttons, and track click-through analytics.",
    color: "#BB8FCE",
    category: "Tools"
  },
  {
    icon: <Contacts fontSize="large" />,
    title: "Contact Management & CRM",
    description: "Store creator/brand profiles, deal history, notes, pipeline views, and automated follow-up triggers.",
    color: "#85C1E9",
    category: "Management"
  },
  {
    icon: <SyncAlt fontSize="large" />,
    title: "Zap Feed Posting",
    description: "Native Zapier integration to auto-queue posts across channels based on events and approvals.",
    color: "#F8C471",
    category: "Automation"
  }
];

export default function Create() {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.down("lg"));
  const refs = useRef({});

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.substring(1);
      refs.current[id]?.scrollIntoView({ behavior: "smooth" });
    }
  }, [location.hash]);

  const handleNavigate = () => navigate(routes.onboard);

  const CreativeModuleCard = ({ icon, title, description, color, category }) => (
    <Card
      component={motion.div}
      variants={scaleIn}
      whileHover={{ y: -8, rotate: 1 }}
      sx={{
        height: "100%",
        borderRadius: 4,
        overflow: "hidden",
        position: "relative",
        background: `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
        border: `2px solid ${color}30`,
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        "&:hover": {
          boxShadow: `0 20px 40px ${color}40`,
          transform: "translateY(-8px) rotate(1deg)",
        }
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: 0,
          right: 0,
          width: 60,
          height: 60,
          background: `linear-gradient(135deg, ${color} 0%, ${color}80 100%)`,
          borderBottomLeftRadius: 20,
        }}
      />
      <CardContent sx={{ p: 3, height: "100%" }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", mb: 2 }}>
          <Box sx={{ color: color, mr: 2, mt: 0.5 }}>{icon}</Box>
          <Box sx={{ flex: 1 }}>
            <Chip
              label={category}
              size="small"
              sx={{
                backgroundColor: `${color}20`,
                color: color,
                fontWeight: 600,
                mb: 1,
                fontSize: "0.7rem"
              }}
            />
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: theme.palette.text.primary }}>
              {title}
            </Typography>
          </Box>
        </Box>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.6 }}>
          {description}
        </Typography>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ backgroundColor: theme.palette.background.default, overflow: "hidden" }}>
      <HomeHeader />
      <Toolbar />

      {/* Creative Hero Section */}
      <Box
        component="section"
        ref={el => (refs.current['hero'] = el)}
        sx={{
          py: { xs: 8, md: 12 },
          color: "#2f2f22",
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "url('data:image/svg+xml,<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 100 100\"><defs><pattern id=\"grain\" width=\"100\" height=\"100\" patternUnits=\"userSpaceOnUse\"><circle cx=\"25\" cy=\"25\" r=\"1\" fill=\"%23ffffff\" opacity=\"0.1\"/><circle cx=\"75\" cy=\"75\" r=\"1\" fill=\"%23ffffff\" opacity=\"0.1\"/><circle cx=\"50\" cy=\"10\" r=\"0.5\" fill=\"%23ffffff\" opacity=\"0.1\"/></pattern></defs><rect width=\"100\" height=\"100\" fill=\"url(%23grain)\"/></svg>') repeat",
            opacity: 0.3,
          }
        }}
      >
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <Grid container alignItems="center" spacing={{ xs: 4, md: 6 }}>
            {/* Left side - Text content */}
            <Grid item xs={12} lg={6}>
              <Box sx={{ textAlign: { xs: "center", lg: "left" } }}>
                <motion.div variants={slideIn} initial="hidden" animate="visible">
                  <Typography
                    variant="h1"
                    sx={{
                      mb: 3,
                      fontFamily: "Moret Variable",
                      fontWeight: 500,
                      fontSize: { xs: "2.5rem", sm: "3rem", md: "3.5rem", lg: "4rem" },
                      lineHeight: 1.1,
                      color: theme.palette.text.primary,
                    }}
                  >
                    Unleash Your
                    <br />
                    Creative Vision
                  </Typography>
                </motion.div>
                <motion.div variants={slideIn} initial="hidden" animate="visible" transition={{ delay: 0.2 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 5,
                      fontWeight: 400,
                      opacity: 0.9,
                      maxWidth: { xs: "100%", lg: 500 },
                      mx: { xs: "auto", lg: 0 },
                      fontSize: { xs: "1rem", sm: "1.125rem", md: "1.25rem" },
                      lineHeight: 1.7,
                      color: theme.palette.text.secondary,
                    }}
                  >
                    Transform ideas into stunning content with our comprehensive creative workflow platform
                  </Typography>
                </motion.div>
                <motion.div variants={slideIn} initial="hidden" animate="visible" transition={{ delay: 0.4 }}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleNavigate}
                    sx={{
                      fontSize: { xs: "1rem", md: "1.125rem" },
                      px: { xs: 5, md: 8 },
                      py: { xs: 2, md: 2.5 },
                      fontWeight: 700,
                      borderRadius: "50px",
                      textTransform: "none",
                      boxShadow: "0 12px 40px rgba(0,0,0,0.15)",
                      position: "relative",
                      overflow: "hidden",
                      '&::before': {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: "-100%",
                        width: "100%",
                        height: "100%",
                        background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)",
                        transition: "left 0.5s",
                      },
                      '&:hover': {
                        bgcolor: theme.palette.primary.light,
                        transform: 'translateY(-4px) scale(1.02)',
                        boxShadow: '0 16px 56px rgba(0,0,0,0.2)',
                        '&::before': {
                          left: '100%'
                        }
                      },
                      transition: "all 0.3s ease"
                    }}
                  >
                    <span style={{ position: "relative", zIndex: 1 }}>
                      Start Creating
                    </span>
                  </Button>
                </motion.div>
              </Box>
            </Grid>

            {/* Right side - Hero Image */}
            <Grid item xs={12} lg={6} sx={{ display: { xs: "none", md: "block" } }}>
              <motion.div
                variants={scaleIn}
                initial="hidden"
                animate="visible"
                transition={{ delay: 0.3, duration: 0.6 }}
              >
                <Box
                  sx={{
                    position: "relative",
                    borderRadius: { xs: "16px", md: "24px" },
                    overflow: "hidden",
                    
                    transform: { lg: "perspective(1000px) rotateY(-5deg)" },
                    transition: "transform 0.4s ease",
                    "&:hover": {
                      transform: { lg: "perspective(1000px) rotateY(0deg) scale(1.02)" },
                    },
                  }}
                >
                  <img
                    src={heroImage}
                    alt="Creative Dashboard"
                    style={{
                      width: '100%',
                      height: 'auto',
                      display: 'block',
                      borderRadius: 'inherit',
                    }}
                  />
                  {/* Subtle gradient overlay */}
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: "linear-gradient(135deg, rgba(255,255,255,0.1) 0%, transparent 50%)",
                      pointerEvents: "none",
                    }}
                  />
                </Box>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Creative Modules - Masonry Style */}
      <Box
        id="modules"
        ref={el => (refs.current['modules'] = el)}
        sx={{ py: { xs: 8, md: 10 }, bgcolor: theme.palette.background.default }}
      >
        <Container maxWidth="xl">
          <motion.div variants={fadeIn} initial="hidden" animate="visible">
            <Typography
              variant="h2"
              align="center"
              sx={{
                fontWeight: 800,
                mb: 2,
                fontFamily:"Moret Variable" ,fontWeight:'600',
               fontSize: { xs: "2.5rem", md: "3.5rem", lg: "4rem"},
                color: theme.palette.text.primary,
              }}
            >
              Creative Powerhouse
            </Typography>
            <Typography
              variant="h6"
              align="center"
              sx={{ 
                mb: 8,
                color: theme.palette.text.secondary,
                fontSize: { xs: "1rem", md: "1.125rem"},
               }}
            >
              Everything you need to bring your creative vision to life
            </Typography>
          </motion.div>

          {/* Bento Grid Layout */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)",
                lg: "repeat(4, 1fr)",
              },
              gap: { xs: 2, sm: 2.5, md: 3 },
              gridAutoRows: "minmax(180px, auto)",
            }}
          >
            {modules.map((mod, idx) => {
              // Define bento grid spans for each card
              const getGridSpan = (index) => {
                const spans = {
                  0: { col: { xs: 1, sm: 2, md: 2, lg: 2 }, row: 1 },      // Creative Briefs - wide
                  1: { col: { xs: 1, sm: 2, md: 1, lg: 2 }, row: 1 },      // Asset Library - wide
                  2: { col: { xs: 1, sm: 1, md: 1, lg: 1 }, row: 1 },      // Review Workflows
                  3: { col: { xs: 1, sm: 1, md: 1, lg: 1 }, row: 1 },      // Content Calendar
                  4: { col: { xs: 1, sm: 2, md: 2, lg: 2 }, row: 1 },      // AI-Assisted - wide
                  5: { col: { xs: 1, sm: 1, md: 1, lg: 1 }, row: 1 },      // Team Collaboration
                  6: { col: { xs: 1, sm: 1, md: 1, lg: 1 }, row: 1 },      // Integrations
                  7: { col: { xs: 1, sm: 1, md: 1, lg: 1 }, row: 1 },      // Reporting
                  8: { col: { xs: 1, sm: 1, md: 1, lg: 1 }, row: 1 },      // Link-in-Bio
                  9: { col: { xs: 1, sm: 2, md: 2, lg: 2 }, row: 1 },      // Contact Management - wide
                  10: { col: { xs: 1, sm: 2, md: 1, lg: 2 }, row: 1 },     // Zap Feed - wide
                };
                return spans[index] || { col: { xs: 1, sm: 1, md: 1, lg: 1 }, row: 1 };
              };

              const span = getGridSpan(idx);

              return (
                <Box
                  key={idx}
                  sx={{
                    gridColumn: {
                      xs: `span ${span.col.xs}`,
                      sm: `span ${span.col.sm}`,
                      md: `span ${span.col.md}`,
                      lg: `span ${span.col.lg}`,
                    },
                    gridRow: `span ${span.row}`,
                  }}
                >
                  <CreativeModuleCard
                    icon={mod.icon}
                    title={mod.title}
                    description={mod.description}
                    color={mod.color}
                    category={mod.category}
                  />
                </Box>
              );
            })}
          </Box>
        </Container>
      </Box>

      {/* Creative CTA */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          position: "relative",
          overflow: "hidden",
          background: `
            radial-gradient(circle at 50% 50%, rgba(15, 23, 42, 0.08) 0%, transparent 50%),
            radial-gradient(circle at 20% 80%, rgba(30, 41, 59, 0.06) 0%, transparent 40%),
            radial-gradient(circle at 80% 20%, rgba(51, 65, 85, 0.05) 0%, transparent 40%)
          `,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "-50%",
            left: "-50%",
            width: "200%",
            height: "200%",
            // background: "radial-gradient(circle, rgba(255,107,107,0.3) 0%, transparent 70%)",
            animation: "float 6s ease-in-out infinite",
          }}
        />
        <Container maxWidth="md" sx={{ position: "relative", zIndex: 1 }}>
          <Box textAlign="center" sx={{  }}>
            <motion.div variants={scaleIn} initial="hidden" animate="visible">
              <Typography
                variant={isMobile ? "h3" : "h2"}
                sx={{
                  fontWeight: 900,
                  mb: 3,
                  color:theme.palette.text.primary,
                  textShadow: "0 2px 20px rgba(0,0,0,0.3)",
                }}
              >
                Ready to Create Magic?
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  mb: 6,
                  fontWeight: 300,
                  opacity: 0.95,
                  textShadow: "0 1px 10px rgba(0,0,0,0.2)",
                  color: theme.palette.text.secondary,
                }}
              >
                Join thousands of creators and brands crafting extraordinary content
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={handleNavigate}
                sx={{
                  fontSize: { xs: "1rem", md: "1.125rem" },
                  px: { xs: 5, md: 8 },
                  py: { xs: 2, md: 2.5 },
                  fontWeight: 700,
                  borderRadius: "50px",
                  textTransform: "none",
                  boxShadow: "0 12px 40px rgba(0,0,0,0.15)",
                  position: "relative",
                  overflow: "hidden",
                  '&::before': {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: "-100%",
                    width: "100%",
                    height: "100%",
                    background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)",
                    transition: "left 0.5s",
                  },
                  '&:hover': {
                    bgcolor: theme.palette.primary.light,
                    transform: 'translateY(-4px) scale(1.02)',
                    boxShadow: '0 16px 56px rgba(0,0,0,0.2)',
                    '&::before': {
                      left: '100%'
                    }
                  },
                  transition: "all 0.3s ease"
                }}
              >
                <span style={{ position: "relative", zIndex: 1 }}>
                  Begin Your Journey
                </span>
              </Button>
            </motion.div>
          </Box>
        </Container>
      </Box>

      <HomeFooter />

      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translate(0px, 0px) rotate(0deg); }
          33% { transform: translate(30px, -30px) rotate(1deg); }
          66% { transform: translate(-20px, 20px) rotate(-1deg); }
        }
      `}</style>
    </Box>
  );
}